import React, { useContext } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { SearchResultPlan, PlanCategoryType } from "../../@types";
import { SuppressionHandler } from "..";
import {
  ifNumber,
  useTranslate,
  isMaPlanType,
  planDataUtils,
} from "../../helpers";
import { AppContext } from "../../app/store";
import Tooltip from "../Tooltip";
import { calculateYourResponsibility } from "../../helpers/planHelpers";

interface OtherCostsProps {
  plan: SearchResultPlan;
}

const OtherCosts: React.FunctionComponent<OtherCostsProps> = ({ plan }) => {
  const t = useTranslate();
  const { state } = useContext(AppContext);
  const { mctCurrentPlanYear } = useFlags();

  return (
    <div className="PlanCard__section" data-testid="otherCosts">
      <h3 className="PlanCard__section_heading">
        {t("plan_card.section_heading.other_costs")}
      </h3>
      <div className="PlanCard__info_group__otherCosts">
        <span className="PlanCard__data">
          <SuppressionHandler plan={plan}>
            {ifNumber(plan.annual_deductible)}
          </SuppressionHandler>
        </span>
        <Tooltip
          triggerText={t("plan_terms.health_plan_deductible")}
          title={t("plan_terms.health_plan_deductible")}
        >
          {t(
            plan.category === PlanCategoryType.MSA
              ? "msa_plan.health_deductible_tooltip"
              : "plan_card.tooltip.health_deductible"
          )}
        </Tooltip>
      </div>
      {plan.category === PlanCategoryType.MSA && (
        <>
          <div className="PlanCard__info_group">
            <span className="PlanCard__data">
              <SuppressionHandler plan={plan}>
                {ifNumber(plan.msa_annual_deposit)}
              </SuppressionHandler>
            </span>
            <Tooltip
              triggerText={t("msa_plan.yearly_deposit")}
              title={t("msa_plan.yearly_deposit")}
            >
              {t("msa_plan.yearly_deposit_tooltip")}
            </Tooltip>
          </div>
          <div className="PlanCard__info_group">
            <span className="PlanCard__data">
              <SuppressionHandler plan={plan}>
                {ifNumber(
                  calculateYourResponsibility(
                    plan.annual_deductible,
                    plan.msa_annual_deposit
                  )
                )}
              </SuppressionHandler>
            </span>
            <Tooltip
              triggerText={t("msa_plan.your_responsibility")}
              title={t("msa_plan.your_responsibility")}
            >
              {t("msa_plan.your_responsibility_tooltip")}
            </Tooltip>
          </div>
        </>
      )}
      {isMaPlanType(plan.plan_type) ? (
        <div className="PlanCard__info_group">
          <span>
            {t("plan_terms.drug_deductible")}
            {": "}
          </span>
          <span>
            {" "}
            {[
              PlanCategoryType.COST_1876,
              PlanCategoryType.PFFS,
              PlanCategoryType.MSA,
            ].includes(plan.category)
              ? t("plan_card.pffs_cost.drug_deductible")
              : t("plan_terms.see_what_drugs_are_covered_ma")}
          </span>
        </div>
      ) : (
        <div className="PlanCard__info_group">
          <span className="PlanCard__data">
            <SuppressionHandler plan={plan}>
              {planDataUtils.getAnnualDrugDeductible(
                state,
                mctCurrentPlanYear,
                plan
              )}
            </SuppressionHandler>
          </span>
          <Tooltip
            triggerText={t("plan_terms.drug_deductible")}
            title={t("plan_terms.drug_deductible")}
          >
            {t("plan_card.tooltip.drug_deductible")}
          </Tooltip>
        </div>
      )}
      <div className="PlanCard__info_group">
        <span>
          <SuppressionHandler plan={plan}>
            <span
              className="PlanCard__data ds-u-margin-right--1"
              dangerouslySetInnerHTML={{ __html: plan.maximum_oopc }}
            />
          </SuppressionHandler>
          <span className="ds-u-display--inline-block">
            <Tooltip
              triggerText={t("plan_terms.oopc_maximum")}
              title={t("plan_terms.oopc_maximum")}
            >
              {t("plan_card.tooltip.oopc_maximum")}
            </Tooltip>
          </span>
        </span>
      </div>
    </div>
  );
};

export default OtherCosts;
